import React, { useState, useRef, useEffect } from 'react';
import CodeEditor from './CodeEditor';
import { useAuth } from './context/AuthContext';

function Cell({ cell, onUpdate, onDelete, onExecute, onMove, readOnly = false, isExecuting = false }) {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(cell.content);
  // const [isHovered, setIsHovered] = useState(false); // Removed unused state
  const [isLocalExecuting, setIsLocalExecuting] = useState(false);
  const [isCodeCollapsed, setIsCodeCollapsed] = useState(false);
  const [isOutputCollapsed, setIsOutputCollapsed] = useState(false);

  // Temporary output state to handle clearing on execution
  const [displayedOutput, setDisplayedOutput] = useState(cell.output);
  const [displayedError, setDisplayedError] = useState(cell.error);

  const { user } = useAuth();

  // Get hide_code setting from user settings
  const hideCode = user?.settings?.hide_code || false;

  const textareaRef = useRef(null);

  useEffect(() => {
    if (!isEditing) {
      setContent(cell.content);
    }
  }, [cell.content, isEditing]);

  useEffect(() => {
    // When cell finishes executing (or updates), update displayed output
    if (!isExecuting && !isLocalExecuting) {
      setDisplayedOutput(cell.output);
      setDisplayedError(cell.error);
    }
  }, [cell.output, cell.error, isExecuting, isLocalExecuting]);

  // Debounced auto-save
  useEffect(() => {
    if (isEditing && content !== cell.content) {
      const timer = setTimeout(() => {
        onUpdate(cell.cell_id, content);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [content, isEditing, cell.content, cell.cell_id, onUpdate]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      adjustTextareaHeight();
    }
  }, [isEditing]);

  // Reset executing state when execution_count changes (indicating completion)
  useEffect(() => {
    setIsLocalExecuting(false);
  }, [cell.execution_count]);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  const handleSave = async () => {
    if (content !== cell.content) {
      await onUpdate(cell.cell_id, content);
    }
    setIsEditing(false);
  };

  const execute = async () => {
    if (isEditing) {
      await handleSave();
    }
    setIsEditing(false);
    setIsLocalExecuting(true);
    // Optimistically clear output
    setDisplayedOutput(null);
    setDisplayedError(null);
    setIsOutputCollapsed(false); // Auto expand on run
    await onExecute(cell.cell_id);
  };

  const handleKeyDown = (e) => {
    // This handles events from textarea or simulated from CodeEditor
    if (e.key === 'Escape') {
      setContent(cell.content);
      setIsEditing(false);
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault && e.preventDefault();
      if (cell.cell_type === 'code') {
        execute();
      } else {
        handleSave();
      }
    }
  };

  const renderMarkdown = (text) => {
    const parseInline = (str) => {
      const parts = str.split(/(\*\*.*?\*\*)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };

    return text.split('\n').map((line, i) => {
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-3xl font-bold mb-4">{parseInline(line.slice(2))}</h1>;
      } else if (line.startsWith('## ')) {
        return <h2 key={i} className="text-2xl font-bold mb-3">{parseInline(line.slice(3))}</h2>;
      } else if (line.startsWith('### ')) {
        return <h3 key={i} className="text-xl font-bold mb-2">{parseInline(line.slice(4))}</h3>;
      } else if (line.trim() === '') {
        return <br key={i} />;
      } else {
        return <p key={i} className="mb-2">{parseInline(line)}</p>;
      }
    });
  };

  const cellIsExecuting = isExecuting || isLocalExecuting;

  return (
    <div
      className={`group relative bg-white rounded-lg border transition-all my-4 ${cellIsExecuting
        ? 'border-yellow-400 shadow-lg shadow-yellow-100'
        : cell.error || displayedError
          ? 'border-red-300'
          : 'border-gray-200 hover:border-blue-300'
        }`}
    // onMouseEnter={() => setIsHovered(true)}
    // onMouseLeave={() => setIsHovered(false)}
    >
      {/* Sidebar/Gutter (Kaggle Style) */}
      <div className="absolute -left-12 top-0 bottom-0 w-10 flex flex-col gap-1 pt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {!readOnly && (
          <>
            <button onClick={() => onMove && onMove(cell.cell_id, 'up')} className="p-1 hover:bg-gray-200 rounded text-gray-500" title="Move Up">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
            </button>
            <button onClick={() => onMove && onMove(cell.cell_id, 'down')} className="p-1 hover:bg-gray-200 rounded text-gray-500" title="Move Down">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
          </>
        )}
      </div>

      {/* Cell Header / Toolbar */}
      <div className={`flex items-center justify-between px-4 py-2 ${hideCode && cell.cell_type === 'code'
        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200'
        : cellIsExecuting
          ? 'bg-yellow-50 border-yellow-200'
          : 'bg-gray-50 border-gray-100'
        } border-b text-xs`}>
        <div className="flex items-center gap-3">
          {cell.cell_type === 'code' && !hideCode && (
            <button
              onClick={execute}
              disabled={cellIsExecuting}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md font-medium transition-all ${cellIsExecuting
                ? 'bg-yellow-100 text-yellow-700 cursor-not-allowed'
                : hideCode
                  ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm hover:shadow-md'
                  : 'bg-green-50 hover:bg-green-100 text-green-700'
                }`}
              title="Run (Ctrl+Enter)"
            >
              {cellIsExecuting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {hideCode && <span>Running...</span>}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  {hideCode && <span>Run</span>}
                </>
              )}
            </button>
          )}

          {!hideCode && (
            <>
              <span className="font-mono text-gray-500">
                [{cell.execution_count || ' '}]
              </span>
              <span className="uppercase tracking-wider font-semibold text-[10px] text-gray-400">
                {cell.cell_type}
              </span>
            </>
          )}

        </div>

        <div className="flex items-center gap-2">
          {!hideCode && (
            <>
              <button
                onClick={() => setIsCodeCollapsed(!isCodeCollapsed)}
                className={`p-1 rounded hover:bg-gray-200 text-gray-500 ${isCodeCollapsed ? 'bg-gray-200' : ''}`}
                title={isCodeCollapsed ? "Expand Code" : "Collapse Code"}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </button>
            </>
          )}

          {(displayedOutput || displayedError) && (
            <button
              onClick={() => setIsOutputCollapsed(!isOutputCollapsed)}
              className={`p-1.5 rounded-md transition-colors ${hideCode
                ? 'hover:bg-blue-100 text-blue-600'
                : 'hover:bg-gray-200 text-gray-500'
                } ${isOutputCollapsed ? (hideCode ? 'bg-blue-100' : 'bg-gray-200') : ''}`}
              title={isOutputCollapsed ? "Show Results" : "Hide Results"}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isOutputCollapsed ? "M19 9l-7 7-7-7" : "M5 15l7-7 7 7"} />
              </svg>
            </button>
          )}

          {!readOnly && (
            <button
              onClick={() => onDelete(cell.cell_id)}
              className="p-1.5 hover:bg-red-100 rounded-md text-gray-400 hover:text-red-600 transition-colors ml-1"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Cell Content */}
      {!isCodeCollapsed && !(hideCode && cell.cell_type === 'code') && (
        <div className="p-4">
          {isEditing ? (
            <div>
              {cell.cell_type === 'code' ? (
                <>
                  <CodeEditor
                    value={content}
                    onChange={(newValue) => setContent(newValue)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    language="python"
                  />
                </>
              ) : (
                <>
                  <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => {
                      setContent(e.target.value);
                      adjustTextareaHeight();
                    }}
                    onKeyDown={handleKeyDown}
                    onBlur={handleSave}
                    className="w-full p-3 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    style={{ minHeight: '100px' }}
                  />
                </>
              )}
            </div>
          ) : (
            <div
              onClick={() => !readOnly && setIsEditing(true)}
              className={`${!readOnly ? 'cursor-pointer' : ''} min-h-[60px]`}
            >
              {cell.cell_type === 'markdown' ? (
                <div className="prose max-w-none">
                  {renderMarkdown(cell.content)}
                </div>
              ) : (
                <CodeEditor
                  value={cell.content}
                  onChange={() => { }}
                  readOnly={true}
                  language="python"
                  onKeyDown={handleKeyDown}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Collapsed Code Placeholder */}
      {(isCodeCollapsed || (hideCode && cell.cell_type === 'code')) && (
        <div
          className={` ${hideCode
            ? ''
            : 'bg-gray-50'
            } text-center group/code cursor-pointer transition-all hover:bg-opacity-80`}
          onClick={() => {
            if (isCodeCollapsed) setIsCodeCollapsed(false);
          }}
        >
          { !(hideCode && cell.cell_type === 'code') &&  (
            <div className="flex items-center justify-center gap-2 text-gray-400 italic text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <span>Code collapsed (click to expand)</span>
            </div>
          )}
        </div>
      )}


      {/* Cell Output */}
      {!isOutputCollapsed && (
        <>
          {displayedError && !(hideCode && cell.cell_type === 'code') && (
            <div className="border-t border-red-200 bg-red-50 p-4">
              <div className="flex items-center gap-2 text-sm text-red-700 mb-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Execution Error:</span>
              </div>
              <div className="font-mono text-sm text-red-600 bg-white p-3 rounded border border-red-200 max-h-[300px] overflow-auto">
                {displayedError}
              </div>
            </div>
          )}

          {displayedOutput && cell.cell_type === 'code' && !displayedError &&
            !(hideCode && cell.cell_type === 'code' && displayedOutput.trim() === "Code executed successfully") && (
              <div className={`border-t ${hideCode ? 'border-blue-100 bg-white' : 'border-gray-200 bg-gray-50'
                } `}>

                <div className={`text-sm ${hideCode ? 'text-gray-800' : 'font-mono text-gray-700'
                  } bg-white p-4 rounded-lg border ${hideCode ? 'border-blue-100 shadow-sm' : 'border-gray-200'
                  } overflow-x-auto max-h-[500px] overflow-y-auto`}>
                  {displayedOutput.split('---IMAGE_START---').map((part, index) => {
                    if (part.includes('---IMAGE_END---')) {
                      const [imgData, rest] = part.split('---IMAGE_END---');
                      return (
                        <React.Fragment key={index}>
                          <img src={imgData.trim()} alt="Plot" className={`my-2 max-w-full ${hideCode ? 'rounded-lg shadow-md' : ''
                            }`} />
                          {rest && <pre className={hideCode ? "whitespace-pre-wrap font-mono text-xs mt-2" : "whitespace-pre-wrap"}>{rest}</pre>}
                        </React.Fragment>
                      );
                    }
                    return <pre key={index} className={hideCode ? "whitespace-pre-wrap font-mono text-xs" : "whitespace-pre-wrap"}>{part}</pre>;
                  })}
                </div>
              </div>
            )}
        </>
      )}

      {/* Collapsed Output Placeholder */}
      {isOutputCollapsed && (displayedOutput || displayedError) && (
        <div
          className="border-t border-gray-200 bg-gray-50 p-2 text-xs text-gray-400 cursor-pointer text-center italic hover:bg-gray-100"
          onClick={() => setIsOutputCollapsed(false)}
        >
          Output hidden (click to expand)
        </div>
      )}

    </div>
  );
}

export default Cell;